/**
 * PublicationsList component for displaying and filtering publications
 * Similar to CompaniesList and ArticlesList with filters
 */

'use client';

import { FileText } from 'lucide-react';
import { PublicationsFilters } from '../filters/publications-filters';
import { usePublicationsFilter } from '../hooks/use-publications-filter';
import type { PublicationsListProps } from '../types/publication';
import { PublicationCard } from './PublicationCard';
import { Pagination } from './ui/pagination';

export function PublicationsList({
  publications,
  baseUrl = '/publications',
  pageSize = 12,
  showFilters = true,
  enabledFilters = { search: true, themes: true },
}: PublicationsListProps) {
  const {
    query,
    selectedThemes,
    currentPage,
    themes,
    visiblePublications,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    setQuery,
    toggleTheme,
    clearThemeSelection,
    handlePageChange,
    clearAllFilters,
  } = usePublicationsFilter({ publications, pageSize });

  const content = (
    <main className="space-y-6 w-full min-w-0">
      {/* Screen reader announcement for filter changes */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {hasResults
          ? `${totalResults} ${totalResults === 1 ? 'publication' : 'publications'} found`
          : hasActiveFilters
            ? 'No publications match your filters'
            : 'No publications available'}
      </div>

      {/* Results Summary */}
      {hasResults && (
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          <p>
            Showing {startIndex + 1}–{endIndex} of {totalResults}{' '}
            {totalResults === 1 ? 'publication' : 'publications'}
          </p>
        </div>
      )}

      {/* Publications Grid */}
      {hasResults ? (
        <>
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visiblePublications.map(publication => (
              <li key={publication.id} className="w-full min-w-0">
                <PublicationCard publication={publication} searchTerm={normalizedQuery} />
              </li>
            ))}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <div className="flex min-h-[300px] flex-col items-center justify-center rounded-lg border border-dashed text-center p-8">
          <FileText className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-lg font-medium text-muted-foreground">
            {hasActiveFilters
              ? 'No publications match your filters.'
              : 'No publications available.'}
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="mt-4 text-sm font-medium text-primary hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
    </main>
  );

  if (!showFilters) {
    return <div className="w-full">{content}</div>;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] w-full max-w-full">
      {/* Publications Grid */}
      <div className="lg:col-start-1 w-full min-w-0">{content}</div>

      {/* Filters Sidebar */}
      <aside className="w-full lg:col-start-2 lg:row-start-1 lg:w-80 lg:justify-self-end">
        <PublicationsFilters
          query={query}
          onQueryChange={setQuery}
          themes={themes}
          selectedThemes={selectedThemes}
          onToggleTheme={toggleTheme}
          onClearThemeSelection={clearThemeSelection}
          totalResults={totalResults}
          hasActiveFilters={hasActiveFilters}
          onClearAllFilters={clearAllFilters}
          enabledFilters={enabledFilters}
        />
      </aside>
    </div>
  );
}
